import React from "react";

// ─── Types ────────────────────────────────────────────────────────────────────

export type CheckboxSize = "sm" | "md" | "lg";

export interface CheckboxProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size" | "type"> {
  size?: CheckboxSize;
  indeterminate?: boolean;
}

// ─── Size config ──────────────────────────────────────────────────────────────

const SIZES: Record<
  CheckboxSize,
  { outer: number; radius: number; borderInactive: number; borderActive: number }
> = {
  sm: { outer: 16, radius: 2, borderInactive: 1.2, borderActive: 1.2 },
  md: { outer: 20, radius: 4, borderInactive: 1.5, borderActive: 1.8 },
  lg: { outer: 24, radius: 4, borderInactive: 1.5, borderActive: 1.8 },
};

// ─── Icons ────────────────────────────────────────────────────────────────────

function CheckIcon({ size }: { size: CheckboxSize }) {
  const sw = size === "sm" ? 1.4 : 1.6;
  return (
    <svg viewBox="0 0 10 10" fill="none" width="100%" height="100%" aria-hidden="true">
      <path
        d="M1.5 5.5L4 7.5L8.5 2.5"
        stroke="white"
        strokeWidth={sw}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function DashIcon({ size }: { size: CheckboxSize }) {
  const sw = size === "sm" ? 1.4 : 1.6;
  return (
    <svg viewBox="0 0 10 10" fill="none" width="100%" height="100%" aria-hidden="true">
      <path
        d="M1.5 5H8.5"
        stroke="white"
        strokeWidth={sw}
        strokeLinecap="round"
      />
    </svg>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export function Checkbox({
  size = "sm",
  indeterminate = false,
  checked,
  defaultChecked,
  onChange,
  disabled,
  id,
  onFocus,
  onBlur,
  style,
  ...rest
}: CheckboxProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [internalChecked, setInternalChecked] = React.useState(defaultChecked ?? false);
  const [focused, setFocused] = React.useState(false);

  const isControlled = checked !== undefined;
  const isChecked = isControlled ? !!checked : internalChecked;

  React.useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  const { outer, radius, borderInactive, borderActive } = SIZES[size];
  const isActive = isChecked || indeterminate;
  const borderWidth = isActive ? borderActive : borderInactive;

  const containerStyle: React.CSSProperties = {
    position: "relative",
    display: "inline-flex",
    flexShrink: 0,
    width: outer,
    height: outer,
    overflow: "hidden",
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1,
    outline: focused ? `2px solid var(--color-border-focus)` : "none",
    outlineOffset: 2,
    borderRadius: radius,
    ...style,
  };

  const boxStyle: React.CSSProperties = {
    position: "absolute",
    inset: "12.5%",
    borderRadius: radius,
    border: `${borderWidth}px solid ${
      isActive ? "var(--color-bg-secondary)" : "var(--color-border-secondary)"
    }`,
    backgroundColor: isActive ? "var(--color-bg-secondary)" : "transparent",
    boxSizing: "border-box",
    transition: "background-color 120ms ease, border-color 120ms ease",
  };

  const iconStyle: React.CSSProperties = {
    position: "absolute",
    inset: "25%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  };

  return (
    <div
      style={containerStyle}
      onClick={() => {
        if (!disabled) inputRef.current?.click();
      }}
    >
      <input
        ref={inputRef}
        id={id}
        type="checkbox"
        checked={isControlled ? checked : internalChecked}
        onChange={(e) => {
          if (!isControlled) setInternalChecked(e.target.checked);
          onChange?.(e);
        }}
        disabled={disabled}
        onFocus={(e) => {
          setFocused(true);
          onFocus?.(e);
        }}
        onBlur={(e) => {
          setFocused(false);
          onBlur?.(e);
        }}
        style={{
          position: "absolute",
          opacity: 0,
          width: 0,
          height: 0,
          margin: 0,
          pointerEvents: "none",
        }}
        {...rest}
      />
      <div style={boxStyle} />
      {isActive && (
        <div style={iconStyle}>
          {indeterminate ? <DashIcon size={size} /> : <CheckIcon size={size} />}
        </div>
      )}
    </div>
  );
}
